import type Keycloak from "keycloak-js";
import { EditForm } from "./p-edit-form-component";
import { errorText, GdprInfo, gdprStatusParser } from "./p-gdpr-info-component";

export class AccountManager {
  private baseline?: DTO;

  private readonly firstNameInput: HTMLInputElement;
  private readonly lastNameInput: HTMLInputElement;
  private readonly dobInput: HTMLInputElement;
  private readonly accountCardElement: HTMLElement;
  private readonly accountForm: EditForm;
  private readonly accountBody: HTMLBodyElement;
  private readonly accountGenericError: HTMLDivElement;

  private readonly keycloak: Keycloak;
  private readonly gdprDataAccessComponent: GdprInfo;
  private readonly gdprPortabilityComponent: GdprInfo;
  private readonly logoutAllButton: HTMLElement;
  private readonly logoutAcceptButtonRow: HTMLElement;
  private readonly logoutAcceptButton: HTMLElement;
  private readonly logoutCancelButton: HTMLElement;

  private readonly DOB_ERROR_MAPPING = [
    { name: "dob", attribute: "error-message" },
    { name: "dob_day", attribute: "day-error" },
    { name: "dob_month", attribute: "month-error" },
    { name: "dob_year", attribute: "year-error" },
  ];

  constructor(keycloak: Keycloak) {
    this.firstNameInput = document.getElementById(
      "firstName",
    ) as HTMLInputElement;
    this.lastNameInput = document.getElementById(
      "lastName",
    ) as HTMLInputElement;
    this.dobInput = document.querySelector(
      "dob-input-component",
    ) as HTMLInputElement;
    this.accountCardElement = document.getElementById("account-card");
    this.accountBody = document.querySelector("body") as HTMLBodyElement;
    this.accountGenericError = document.getElementById(
      "generic-error",
    ) as HTMLDivElement;
    this.keycloak = keycloak;
    this.gdprDataAccessComponent = document.querySelector(
      ".gdpr-data-access",
    ) as GdprInfo;
    this.gdprDataAccessComponent.keycloak = keycloak;
    this.gdprPortabilityComponent = document.querySelector(
      ".gdpr-portability",
    ) as GdprInfo;
    this.gdprPortabilityComponent.keycloak = keycloak;
    this.logoutAllButton = document.getElementById("logout-all-button");
    this.logoutAcceptButtonRow = document.getElementById(
      "logout-accept-button-row",
    );
    this.logoutAcceptButtonRow.hidden = true;
    this.logoutAcceptButton = document.getElementById("logout-accept-button");
    this.logoutCancelButton = document.getElementById("logout-cancel-button");

    document
      .getElementById("change-password-button")
      .addEventListener("click", (evt) => {
        evt.preventDefault();
        this.runRequiredAction("UPDATE_PASSWORD");
      });

    document
      .getElementById("change-email-button")
      .addEventListener("click", (evt) => {
        evt.preventDefault();
        this.runRequiredAction("EMAIL_CHANGE_PENNY_FLOW");
      });

    this.logoutAllButton.addEventListener("click", (ev) => {
      ev.preventDefault();
      this.setPositiveFeedback(
        this.logoutAllButton,
        "Möchtest du dich wirklich überall abmelden? Bitte beachte, dass die Abmeldung bis zu 10 Min. dauern kann.",
      );
      this.logoutAllButton.hidden = true;
      this.logoutAcceptButtonRow.hidden = false;
    });

    this.logoutAcceptButton.addEventListener("click", (ev) => {
      ev.preventDefault();
      this.runRequiredAction("logout-all-sessions").catch(() =>
        this.setNegativeFeedback(this.logoutAllButton),
      );
    });

    this.logoutCancelButton.addEventListener("click", (ev) => {
      ev.preventDefault();
      this.logoutAcceptButtonRow.parentNode.querySelector(".alert").remove();
      this.logoutAllButton.hidden = false;
      this.logoutAcceptButtonRow.hidden = true;
    });

    this.accountForm = document.getElementById("account-form") as EditForm;
    this.accountForm.addEventListener("save", (evt) => {
      const detail = (evt as CustomEvent).detail as SaveEventDetail;
      this.saveCurrentState(detail)
        .then(() => {
          this.accountForm.generalError = false;
        })
        .catch((err) => {
          this.accountForm.generalError = true;
          console.error(err);
        });
    });
    this.accountForm.addEventListener("cancel", () => {
      this.resetToSaved(true);
      this.setErrors();
    });
    window.addEventListener("hashchange", this.setContentVisibility);
    this.setContentVisibility();
  }

  private async runRequiredAction(action: Action) {
    await this.keycloak.login({ action });
  }

  private async getToken(): Promise<string> {
    if (this.keycloak.token) {
      return new Promise((resolve) => resolve(this.keycloak.token));
    }
    await this.keycloak.updateToken(5);
    return this.keycloak.token;
  }

  private async getAccountInfo(): Promise<DTO> {
    if (this.baseline) {
      return new Promise((resolve) => resolve(this.baseline));
    }
    const token = await this.getToken();
    const r: Response = await fetch(
      `${this.accountUrl()}?userProfileMetadata=false`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const j: DTO = await r.json();
    this.baseline = j;

    const doiReceivedOn = j.attributes["doi-received-on"]
      ? new Date(j.attributes["doi-received-on"][0])
      : undefined;

    this.gdprDataAccessComponent.userInfo = {
      doiReceivedOn,
      gdprStatus: j.attributes["information-status"]
        ? JSON.parse(j.attributes["information-status"][0], gdprStatusParser)
        : undefined,
    };

    this.gdprPortabilityComponent.userInfo = {
      doiReceivedOn,
      gdprStatus: j.attributes["portability-status"]
        ? JSON.parse(j.attributes["portability-status"][0], gdprStatusParser)
        : undefined,
    };

    return j;
  }

  setPositiveFeedback(button: HTMLElement, text: string = null) {
    const alerts = button.parentNode.querySelectorAll(".alert");
    if (alerts.length > 0) {
      for (let i = 0; i < alerts.length; i++) {
        alerts[i].remove();
      }
    }
    const waitingAlertElement = document.createElement("div");
    waitingAlertElement.setAttribute("role", "alert");
    waitingAlertElement.classList.add("alert");
    waitingAlertElement.classList.add("alert__info");
    waitingAlertElement.innerText =
      text ??
      "Deine Daten werden bereitgestellt. Du erhältst eine Email, sobald das Datenarchiv bereitsteht.";
    button.parentNode.insertBefore(waitingAlertElement.cloneNode(true), button);
    button.hidden = true;
  }

  setNegativeFeedback(button: HTMLElement, text?: string) {
    const alerts = button.parentNode.querySelectorAll(".alert");
    if (alerts.length > 0) {
      for (let i = 0; i < alerts.length; i++) {
        alerts[i].remove();
      }
    }
    const errorAlertElement = document.createElement("div");
    errorAlertElement.setAttribute("role", "alert");
    errorAlertElement.classList.add("alert");
    errorAlertElement.classList.add("alert__error");
    errorAlertElement.innerText =
      text ??
      "Leider können wir deine Anfrage zur Zeit nicht bearbeiten. Wir arbeiten bereits an einer Lösung.";
    button.parentNode.insertBefore(errorAlertElement.cloneNode(true), button);
  }

  resetToSaved(hard?: boolean) {
    if (hard) {
      this.baseline = null;
    }
    this.getAccountInfo()
      .then(() => this.updateUI())
      .catch((err) => {
        console.log(err);
        this.accountBody.hidden = false;
        this.accountGenericError.hidden = false;
      });
  }

  private safeSetTextContent(selector: string, value: string, root?: Element) {
    const target = (root || document).querySelectorAll(selector);

    if (target.length > 0) {
      target.forEach((el) => {
        el.textContent = value;
      });
    }
  }

  private updateUI() {
    this.accountBody.hidden = false;
    this.accountGenericError.hidden = true;
    const { firstName, lastName, attributes, username } = this.baseline;
    setValue(this.firstNameInput, firstName);
    setValue(this.lastNameInput, lastName);
    const dob = attributes?.dob?.at(0);
    setValue(this.dobInput, dob);
    this.dobInput.classList.toggle("filled__date", !!dob);
    const name = [...[firstName], ...[lastName]].join(" ");
    const phonePin = attributes?.phonePin?.at(0) || "Leider nicht verfügbar";

    this.safeSetTextContent(".name", name, this.accountCardElement);
    this.safeSetTextContent(".mail", username, this.accountCardElement);
    this.safeSetTextContent(".phonePin", phonePin);
  }

  private async getCurrentState(detail: SaveEventDetail): Promise<DTO> {
    const base = await this.getAccountInfo();
    return {
      ...base,
      firstName: detail.firstName,
      lastName: detail.lastName,
      attributes: {
        ...base.attributes,
        dob: [detail["user.attributes.dob"]],
      },
    };
  }

  private async saveCurrentState(detail: SaveEventDetail) {
    const [dto, token] = await Promise.all([
      this.getCurrentState(detail),
      this.getToken(),
    ]);
    const response = await fetch(this.accountUrl(), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      method: "POST",
      body: JSON.stringify(dto),
    });
    if (response.status === 400) {
      const json = await response.json();
      this.accountForm.setAttribute("editing", "");
      this.setErrors(json);
    } else {
      this.setErrors();
    }
    this.baseline = dto;
    this.resetToSaved();
    if (response.status > 400) {
      throw new Error(`request failed: ${response.status}`);
    }
  }

  private setErrors(errors?: Errors) {
    const inputs = document.querySelectorAll(".form__input");
    const noErrors = !errors || !errors.errors;
    if (noErrors) {
      this.removeDobError();
      inputs.forEach((input) => {
        const messageElement = input.parentElement.querySelector(
          ".form__error",
        ) as HTMLElement;
        if (!messageElement) {
          return;
        }
        input.classList.remove("form__input--invalid");
        messageElement.hidden = true;
      });
      return;
    }
    const errorsByField = errors.errors.reduce((acc, val) => {
      acc[val.field] = val.errorMessage;
      return acc;
    }, {});
    if (
      errorsByField["dob"] ||
      errorsByField["dob_day"] ||
      errorsByField["dob_month"] ||
      errorsByField["dob_year"]
    ) {
      this.removeDobError();
      this.dobInput.classList.add("form__input--invalid");
      ["dob", "dob_day", "dob_month", "dob_year"].forEach((key) => {
        if (errorsByField[key]) {
          this.setDobError(errorsByField[key], key);
        }
      });
    }
    inputs.forEach((input) => {
      const error = errorsByField[input.id];
      if (input.id.startsWith("dob")) {
        return;
      }
      const messageElement = input.parentElement.querySelector(
        ".form__error",
      ) as HTMLElement;
      if (!messageElement) {
        return;
      }
      if (error) {
        input.classList.add("form__input--invalid");
        messageElement.hidden = false;
        messageElement.textContent = (window as any).l18nMsg[error];
      } else {
        input.classList.remove("form__input--invalid");
        messageElement.hidden = true;
        messageElement.textContent = "";
      }
    });
  }

  private removeDobError() {
    this.dobInput.classList.remove("form__input--invalid");
    this.dobInput.removeAttribute("error-message");
    this.dobInput.removeAttribute("day-error");
    this.dobInput.removeAttribute("month-error");
    this.dobInput.removeAttribute("year-error");
  }

  private setDobError(error: string, field: string) {
    this.dobInput.classList.add("form__input--invalid");
    this.dobInput.setAttribute(
      this.DOB_ERROR_MAPPING.find((e) => e.name === field).attribute,
      (window as any).l18nMsg[error],
    );
  }

  private baseUrl() {
    return `${this.keycloak.authServerUrl}realms/penny`;
  }

  private accountUrl() {
    return `${this.baseUrl()}/account/`;
  }

  private setContentVisibility() {
    let hash = window.location.hash;
    if (
      !hash ||
      hash.startsWith("#state") ||
      hash.startsWith("#&state") ||
      hash.startsWith("#uc-corner-modal-show")
    ) {
      hash = "#menu";
    }
    [
      "menu",
      "personal-info",
      "email-password",
      "ad-preferences",
      "data-and-devices",
      "delete-account",
    ].forEach((id) => {
      const el = document.getElementById(id);
      el.hidden = !hash.startsWith("#" + id);
      if (!el.hidden && hash !== "#menu") {
        el.scrollIntoView(true);
      }
    });
    switch (hash) {
      case "#menu":
        trackSinglePageView("overview");
        break;
      case "#personal-info":
        trackSinglePageView("personal");
        break;
      case "#email-password":
        trackSinglePageView("email_and_password");
        break;
      case "#ad-preferences":
        trackSinglePageView("ad_preferences");
        break;
      case "#data-and-devices":
        trackSinglePageView("data_and_devices");
        break;
      // delete-account not listed in tracking implementation guide -> no tracking
    }
  }
}

function trackSinglePageView(pageName: string) {
  const fn = (window as any).trackSinglePageView;
  if (fn) {
    fn(pageName);
  }
}

function setValue(input: HTMLInputElement, value: string | undefined) {
  input.value = value || "";
  // we need to set this for the input css to work properly
  input.setAttribute("value", value || "");
}

type PortabilityState = "PENDING" | "READY";

export type Action =
  | "EMAIL_CHANGE_PENNY_FLOW"
  | "UPDATE_PASSWORD"
  | "logout-all-sessions"
  | "perform-gdpr-right-of-portability-request";
export type Field = "firstName" | "lastName" | "dob";

type KeyValueArray = {
  [key: string]: string[];
};

interface Attributes {
  dob?: string[];
  phonePin?: string[];
  "doi-received-on"?: string[];
}

interface DTO {
  attributes?: Attributes & KeyValueArray;
  firstName?: string;
  lastName?: string;
  username?: string;
}

interface Errors {
  errors?: {
    field: Field;
    errorMessage: string;
  }[];
}

interface SaveEventDetail {
  firstName: string;
  lastName: string;
  ["user.attributes.dob"]: string;
}
